<template>
  <div>
    <v-progress-linear
      v-if="loading"
      indeterminate
    />
    <TableRemote
      v-else-if="hasPermission('sites.view')"
      :headers="tableHeaders"
      :pagination="pagination"
      :itemActions="itemActions"
      :iconHeader="hasPermission('sites.edit') ? 'add' : null"
      :items="documents"
      :isCustomIcon="true"
      :isInfinitScroll="true"
      :title="$t('sites.document.title')"
      :centerColumns="['action']"
      :notSortableColumns="['action','description', 'files']"
      :sortableColumns="['code', 'type', 'name', 'date']"
      hideDefaultFooter
      isSelectable
      isSearchable
      @iconAction="onClickAction"
      @sendQueryParams="fetchQuery"
    >
      <template #item.files="{ item }">
        <TableFiles :files="item.files" />
      </template>
      <template #withInfiniteScroll>
        <div v-intersect="endIntersect" />
      </template>
    </TableRemote>
    <NoPermissionAlert
      v-else
      :message="$t('permissions.error')"
    />
  </div>
</template>

<script>
import Vue from 'vue';
import { mapGetters } from 'vuex';
import { isEmpty } from 'lodash';
import TableRemote from '@/components/shared/content-views/VTableRemote';
import SiteDocumentForm from '@/views/sites/document/SiteDocumentForm.vue';
import TableFiles from '@/components/shared/content-views/slots/TableFiles';
import NoPermissionAlert from '@/components/shared/NoPermissionAlert';
import ConfirmForm from '@/components/shared/widgets/ConfirmForm.vue';
import roles from '@/constants/roles';
import moment from 'moment';


export default {
    components: {
        TableRemote,
        TableFiles,
        NoPermissionAlert,
    },
    data() {
        return {
            documents: [],
            loading: false,
            pagination: {},
            isInitializing: false,
        };
    },
    computed: {
        ...mapGetters([
            'client',
            'site',
            'lang',
            'hasPermission',
            'siteDocument',
            'teamMainLang',
            'hasRole',
        ]),
        tableHeaders() {
            return [
                {
                    text: this.$t('sites.document.code'),
                    value: 'code',
                    width: '10%',
                    sortable: true,
                },
                {
                    text: this.$t('sites.document.type'),
                    value: 'type',
                    sortable: true,
                },
                {
                    text: this.$t('sites.document.date'),
                    value: 'date',
                    width: '15%',
                    sortable: true,
                },
                {
                    text: this.$t('sites.document.name'),
                    value: 'name',
                    width: '20%',
                    sortable: true,
                },
                {
                    text: this.$t('sites.document.description'),
                    value: 'description',
                    sortable: false,
                },
                {
                    text: this.$t('sites.document.files'),
                    value: 'files',
                    sortable: false,
                },
            ];
        },
        hasSiteId() {
            return Boolean(this.site.id);
        },
        itemActions() {
            let actions;
            if (this.hasRole(roles.LABORATORY_TECHNICIAN.value)
            || this.hasRole(roles.LABORATORY_CHEMIST.value)
            || this.hasRole(roles.SERVICE_TECHNICIAN.value)
            || this.hasRole(roles.ROLE_OPERATOR.value)) {
                actions = [];
            } else {
                actions = [{
                    name: this.$t('forms.btn.edit'),
                    icon: 'edit',
                    mode: 'edit',
                },
                {
                    name: this.$t('forms.btn.delete'),
                    icon: 'delete',
                    mode: 'delete',
                    iconAndTextColor: '#EF4135',
                }];
            }
            return actions;
        },
    },
    watch: {
        async lang() {
            this.pagination = {};
            this.documents = [];
            await this.getDocuments();
        },
        async siteDocument() {
            if (this.$router.currentRoute.matched[3]?.name === 'siteDocument') {
                await this.getDocuments();
            }
        },
    },
    async created() {
        this.loading = true;
        if (!this.hasSiteId) return;
        await this.getDocuments();
        this.loading = false;
    },
    methods: {
        async getDocuments() {
            this.isInitializing = true;
            if (isEmpty(this.pagination) || (this.pagination?.currentPage !== this.queryParams?.page)) {
                const { data } = await this.$rq.getSiteDocuments(this.site.id, this.queryParams);
                const docs = data?.documents?.length ? data.documents.map(doc => this.prepareDocument(doc)) : [];
                this.documents = [...this.documents, ...docs];
                this.pagination = data.pagination;
            }
            this.isInitializing = false;
        },

        async fetchQuery(queryParams) {
            if (!this.isInitializing) {
                this.queryParams = {
                    ...this.queryParams,
                    ...queryParams,
                    page: 1,
                    perpage: queryParams?.perpage || queryParams.perpage,
                    search: queryParams?.search || '',
                    order: this.preparOrderQueryParams(queryParams.order),
                };
                this.pagination = {};
                this.documents = [];
                await this.getDocuments();
            }
        },
        preparOrderQueryParams(value) {
            if (typeof value !== 'string') {
                return null;
            }

            const [name, order] = value.split('_');
            let newValue;
            switch (name) {
                case 'type':
                    newValue = 'type';
                    break;
                case 'code':
                    newValue = 'code';
                    break;
                case 'date':
                    newValue = 'date';
                    break;
                case 'name':
                    newValue = 'name';
                    break;

                default:
                    return null;
            }
            return `${newValue}_${order}`;
        },


        /**
         * Prepare a document for the Data Table component:
         *  - Localize the name and description and type
         *
         * @param {Object} document - Data from the API
         * @param {String} lang
         * @return {Object}
         */
        prepareDocument(document) {
            const { type, name } = document;
            const [selectedFile] = document.files ? document.files.filter(file => file.lang === this.lang || this.teamMainLang) : [];
            const files = [];
            if (!isEmpty(selectedFile)) {
                // eslint-disable-next-line no-underscore-dangle
                files.push({ id: selectedFile.file.id, fileName: selectedFile.file.fileName });
            }
            const description = document?.description ? document.description[this.lang] || document.description[this.teamMainLang] : '';
            return {
                ...document,
                date: document.date ? this.$options.filters.formatDate(document.date, 'L') : '',
                name: name ? name[this.lang] || name[this.teamMainLang] : '',
                files,
                type: type ? type.name[this.lang] || type.name[this.mainLang] : '',
                description,
            };
        },
        /**
         * Prepare a file for the Data Table component
         *
         * @param {Object} file - Data from the API
         * @param {String} lang
         * @return {Object}
         */
        prepareFile(file, lang = null) {
            return {
                ...file,
                lang,
            };
        },

        async editModal(item) {
            const { data } = await this.$rq.getSiteDocument(this.site.id, item.id);

            this.$root.$emit('showModal', {
                title: this.$t('sites.document.edit'),
                component: Vue.component('SiteDocumentForm', SiteDocumentForm),
                showLangs: true,
                isForm: true,
                payload: {
                    populateWith: { ...data, type: data.type.id },
                    editForm: true,
                    isEdition: true,

                },
                onConfirm: async requestPayload => this.onClickUpdate(requestPayload, item.id),
            });
        },

        parseNameDescription(requestPayload) {
            const payload = { ...requestPayload };
            ['name', 'description'].forEach(field => {
                Object.keys(payload[field]).forEach(lang => {
                    payload[field][lang] = payload[field][lang] ?? '';
                });
            });
            return payload;
        },

        async addRequest(requestPayload) {
            const payload = this.parseNameDescription(this.$utils.removeEmptyNullOrUndefined(requestPayload, true));
            const { files } = payload;
            const payloadFiles = [];
            const request = new FormData();
            Object.keys(files).forEach(lang => {
                payloadFiles.push({ fieldName: `file_${lang}` });
                request.append(`file_${lang}`, files[lang]);
            });
            delete payload.files;
            request.append('files', JSON.stringify(payloadFiles));
            Object.keys(payload).forEach(elt => {
                if (typeof payload[elt] === 'object') {
                    request.append(elt, JSON.stringify(payload[elt]));
                } else {
                    request.append(elt, payload[elt]);
                }
            });
            const date = payload.date ? moment(payload.date).toISOString() : moment().toISOString();
            request.set('date', date);
            await this.$rq.addSiteDocument(this.site.id, request);
            this.$toasted.global.toast_success({
                message: this.$t('msg.documentAdded', {
                    data: requestPayload.code,
                }),
            });
            await this.fetchQuery({});
        },
        async onClickUpdate(requestPayload, documentID) {
            // remove null in description and name
            const payload = this.parseNameDescription(this.$utils.removeEmptyNullOrUndefined(requestPayload, true));
            if (payload.date) {
                payload.date = moment(payload.date).toISOString();
            }
            const { files, code } = payload;
            const payloadFiles = [];
            const request = new FormData();

            Object.keys(files).forEach(lang => {
                payloadFiles.push({ fieldName: `file_${lang}` });
                request.append(`file_${lang}`, files[lang]);
            });
            delete payload.files;
            Object.keys(payload).forEach(elt => {
                if (typeof payload[elt] === 'object') {
                    request.append(elt, JSON.stringify(payload[elt]));
                } else {
                    request.append(elt, payload[elt]);
                }
            });

            /* Define parameter for function send data */
            await this.$rq.updateSiteDocument(this.site.id, documentID, request);
            this.$toasted.global.toast_success({
                message: this.$t('msg.documentUpdated', { data: code }),
            });
            await this.fetchQuery({});
        },

        async onClickAction(item, action) {
            if (item === 'add') {
                await this.onClickAdd();
            } else if (action.mode === 'delete') {
                this.$root.$emit('showModal', {
                    title: this.$t('sites.document.remove'),
                    component: Vue.component('ConfirmForm', ConfirmForm),
                    payload: { msg: this.$t('sites.document.confirmRemove') },
                    onConfirm: () => this.removeRequest(item),
                    isForm: false,
                });
            } else if (action.mode === 'edit') {
                this.editModal(item);
            }
        },

        async onClickAdd() {
            this.$root.$emit('showModal', {
                title: this.$t('sites.document.add'),
                component: Vue.component('SiteDocumentForm', SiteDocumentForm),
                showLangs: true,
                onConfirm: requestPayload => this.addRequest(requestPayload),
            });
        },

        async removeRequest(procedure) {
            await this.$rq.deleteSiteDocument(this.site.id, procedure.id);
            this.$toasted.global.toast_success({
                message: this.$t('msg.documentRemoved', { data: procedure.code }),
            });
            await this.fetchQuery({});
        },
        async endIntersect(_, __, isIntersecting) {
            const condition = this.pagination.currentPage < this.pagination.totalPageCount;
            if (isIntersecting && condition) {
                this.queryParams.page = this.pagination.currentPage + 1;
                await this.getDocuments();
                const ref = this.$refs?.tableRemoteRef?.$refs?.tableRemote;
                if (ref?.onScroll) {
                    ref.onScroll();
                }
            }
        },
    },
};
</script>
